import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff, UserPlus } from "lucide-react";
import { useValidateInvitationQuery } from "@/api/queries/admin/InvitationQueries";
import { useAcceptInvitationMutation } from "@/api/mutations/admin/InvitationMutations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { useTranslation } from "react-i18next";

type AcceptFormValues = {
    name: string;
    password: string;
    confirmPassword: string;
};

// Reusable left branding panel
const BrandingPanel = ({ subtitle }: { subtitle?: string }) => (
    <div className="relative hidden flex-1 flex-col bg-muted p-10 text-white lg:flex">
        <div
            className="absolute inset-0"
            style={{
                background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))`,
            }}
        />
        <div className="relative z-20 flex items-center text-3xl font-semibold">
            Junet.io
        </div>
        <div className="relative z-20 flex-1 flex items-center justify-center">
            <UserPlus className="h-48 w-48 text-white" strokeWidth={1} />
        </div>
        <div className="relative z-20">
            <blockquote className="space-y-2">
                <p className="text-lg">AI Made Simple For Your Business</p>
                {subtitle && (
                    <footer className="text-sm opacity-80">{subtitle}</footer>
                )}
            </blockquote>
        </div>
    </div>
);

const AcceptInvitation = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";
    
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isAccepted, setIsAccepted] = useState(false);
    const [createdUserName, setCreatedUserName] = useState("");

    const { data: validation, isLoading: isValidating } = useValidateInvitationQuery(token);

    const acceptMutation = useAcceptInvitationMutation(
        (_, variables) => {
            setCreatedUserName(variables.name);
            setIsAccepted(true);
            toast.success(t("invitations.accept.success"));
        },
        (error) => {
            toast.error(error.message || t("invitations.accept.error"));
        }
    );

    const form = useForm<AcceptFormValues>({
        defaultValues: {
            name: "",
            password: "",
            confirmPassword: "",
        },
    });

    useEffect(() => {
        if (validation?.valid && validation.name) {
            form.setValue("name", validation.name);
        }
    }, [validation, form]);

    const onSubmit = (data: AcceptFormValues) => {
        acceptMutation.mutate({
            token,
            name: data.name,
            password: data.password,
        });
    };

    // Loading state
    if (isValidating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-muted-foreground">{t("invitations.accept.validating")}</p>
                </div>
            </div>
        );
    }

    // No token or invalid
    if (!token || !validation?.valid) {
        return (
            <div className="min-h-screen flex">
                <BrandingPanel />
                
                {/* Right side - Error */}
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="mx-auto flex w-full flex-col items-center justify-center space-y-6 sm:w-[350px]">
                        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                            <XCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <div className="flex flex-col space-y-2 text-center">
                            <h1 className="text-2xl font-semibold tracking-tight">
                                {t("invitations.accept.invalidInvitation")}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {validation?.error || t("invitations.accept.invalidOrExpired")}
                            </p>
                        </div>
                        <Button onClick={() => navigate("/login")} className="w-full">
                            {t("invitations.accept.goToLogin")}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (isAccepted) {
        return (
            <div className="min-h-screen flex">
                <BrandingPanel subtitle="Welcome to the team!" />
                
                {/* Right side - Success */}
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="mx-auto flex w-full flex-col items-center justify-center space-y-6 sm:w-[400px]">
                        <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="flex flex-col space-y-2 text-center">
                            <h1 className="text-2xl font-semibold tracking-tight">
                                {t("invitations.accept.accountCreated")}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {t("invitations.accept.accountCreatedDescription")}
                            </p>
                        </div>
                        
                        {/* Account details */}
                        <div className="w-full rounded-lg border bg-muted/50 p-4 space-y-3">
                            <h3 className="text-sm font-medium text-center">{t("invitations.accept.yourAccountDetails")}</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">{t("invitations.accept.form.name")}:</span>
                                    <span className="font-medium">{createdUserName}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Email:</span>
                                    <span className="font-medium">{validation?.email}</span>
                                </div>
                            </div>
                        </div>
                        
                        <Button onClick={() => navigate("/login")} className="w-full">
                            {t("invitations.accept.proceedToLogin")}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Form
    return (
        <div className="min-h-screen flex">
            <BrandingPanel subtitle="You've been invited to join our platform." />
            
            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
                    <div className="flex flex-col space-y-2 text-center">
                        <h1 className="text-2xl font-semibold tracking-tight">
                            {t("invitations.accept.title")}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {t("invitations.accept.description", { email: validation.email })}
                        </p>
                    </div>
                    
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("invitations.accept.form.name")}</FormLabel>
                                        <FormControl>
                                            <Input 
                                                placeholder={t("invitations.accept.form.namePlaceholder")} 
                                                {...field} 
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("invitations.accept.form.password")}</FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input 
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="••••••••" 
                                                    {...field} 
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </Button>
                                            </div>
                                        </FormControl>
                                        <p className="text-xs text-muted-foreground">
                                            {t("invitations.accept.form.passwordHint")}
                                        </p>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("invitations.accept.form.confirmPassword")}</FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input 
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    placeholder="••••••••" 
                                                    {...field} 
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                >
                                                    {showConfirmPassword ? (
                                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </Button>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button 
                                type="submit" 
                                className="w-full"
                                disabled={acceptMutation.isPending}
                            >
                                {acceptMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t("invitations.accept.form.creating")}
                                    </>
                                ) : (
                                    t("invitations.accept.form.createAccount")
                                )}
                            </Button>
                        </form>
                    </Form>
                    
                    <p className="text-center text-sm text-muted-foreground">
                        {t("invitations.accept.form.alreadyHaveAccount")}{" "}
                        <Button 
                            variant="link" 
                            className="p-0 h-auto text-sm"
                            onClick={() => navigate("/login")}
                        >
                            {t("invitations.accept.form.signIn")}
                        </Button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;
